import { supabase } from '@/lib/supabase';
import {
  PlaybookTemplate,
  PlaybookTemplateTask,
  PlaybookTemplateTaskGroup,
  PlaybookTemplateTaskCustomField,
  PlaybookTemplateTaskGroupMembership,
  PlaybookRun,
  PlaybookTask,
  PlaybookTaskGroup,
  PlaybookTaskCustomField,
  PlaybookTaskComment,
  PlaybookTaskGroupMembership,
  PlaybookPublicLink,
  User,
  RunStatus,
  TaskStatus,
} from '@/types/playbooks';

// Serviços para Templates de Playbook
export const playbookTemplateService = {
  // Listar todos os templates de playbook
  async getBySubscription(): Promise<PlaybookTemplate[]> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Obter um template específico por ID com todas as tarefas relacionadas
  async getById(id: string): Promise<PlaybookTemplate> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Criar um novo template de playbook
  async create(template: Omit<PlaybookTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<PlaybookTemplate> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .insert([template])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Atualizar um template existente
  async update(id: string, template: Partial<PlaybookTemplate>): Promise<PlaybookTemplate> {
    const { data, error } = await supabase
      .from('playbook_templates')
      .update(template)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Excluir um template
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Serviço para tarefas de template
export const playbookTemplateTaskService = {
  // Obter todas as tarefas de um grupo
  async getByGroupId(groupId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('playbook_template_tasks')
      .select('*')
      .eq('playbook_template_group_id', groupId)
      .order('task_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Obter uma tarefa específica
  async getById(id: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('playbook_template_tasks')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Criar uma nova tarefa
  async create(task: {
    playbook_template_group_id: string,
    title: string,
    description?: string,
    task_type?: string,
    assignee_type?: string,
    assignees?: any,
    deadline_type?: string,
    deadline_value?: number,
    deadline_reference?: string,
    detailed_description?: string,
    custom_fields?: any,
    task_order: number
  }): Promise<any> {
    const { data, error } = await supabase
      .from('playbook_template_tasks')
      .insert({
        playbook_template_group_id: task.playbook_template_group_id,
        title: task.title,
        description: task.description || '',
        task_type: task.task_type || 'task',
        assignee_type: task.assignee_type || 'fixed',
        assignees: task.assignees || [],
        deadline_type: task.deadline_type || 'days',
        deadline_value: task.deadline_value || 2,
        deadline_reference: task.deadline_reference || 'launch',
        detailed_description: task.detailed_description || '',
        custom_fields: task.custom_fields || [],
        task_order: task.task_order
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Atualizar uma tarefa existente
  async update(id: string, task: {
    title?: string,
    description?: string,
    task_type?: string,
    assignee_type?: string,
    assignees?: any,
    deadline_type?: string,
    deadline_value?: number,
    deadline_reference?: string,
    detailed_description?: string,
    custom_fields?: any,
    task_order?: number
  }): Promise<any> {
    const { data, error } = await supabase
      .from('playbook_template_tasks')
      .update(task)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Excluir uma tarefa
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_template_tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // Excluir todas as tarefas de um grupo
  async deleteByGroupId(groupId: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_template_tasks')
      .delete()
      .eq('playbook_template_group_id', groupId);

    if (error) throw error;
  }
};

// Serviços para Grupos de Tarefas de Template
export const playbookTemplateTaskGroupService = {
  // Listar todos os grupos de um template
  async getByTemplateId(templateId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('playbook_template_task_groups')
      .select('*')
      .eq('playbook_template_id', templateId)
      .order('group_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Obter um grupo específico
  async getById(id: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('playbook_template_task_groups')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Criar um novo grupo para um template
  async create(group: { playbook_template_id: string, title: string, icon?: string, group_order?: number }): Promise<any> {
    const { data, error } = await supabase
      .from('playbook_template_task_groups')
      .insert({
        playbook_template_id: group.playbook_template_id,
        title: group.title,
        icon: group.icon,
        group_order: group.group_order || 0
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Atualizar um grupo existente
  async update(id: string, group: { title?: string, icon?: string, group_order?: number }): Promise<any> {
    const { data, error } = await supabase
      .from('playbook_template_task_groups')
      .update(group)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Excluir um grupo
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_template_task_groups')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Serviços para Execuções de Playbook
export const playbookRunService = {
  // Listar todas as execuções
  async getAll(): Promise<PlaybookRun[]> {
    const { data, error } = await supabase
      .from('playbook_runs')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as PlaybookRun[];
  },
  
  // Listar todas as execuções para uma subscription
  async getBySubscription(subscriptionId: string): Promise<PlaybookRun[]> {
    const { data, error } = await supabase
      .from('playbook_runs')
      .select('*')
      .eq('subscription_id', subscriptionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as PlaybookRun[];
  },

  // Obter uma execução específica por ID com todas as tarefas relacionadas
  async getById(id: string): Promise<PlaybookRun | null> {
    try {
      // Primeiro busca a execução
      const { data: run, error: runError } = await supabase
        .from('playbook_runs')
        .select('*')
        .eq('id', id)
        .single();

      if (runError) throw runError;
      if (!run) return null;

      // Depois busca as tarefas relacionadas
      const { data: tasks, error: tasksError } = await supabase
        .from('playbook_tasks')
        .select('*')
        .eq('playbook_run_id', id)
        .order('task_order', { ascending: true });

      if (tasksError) throw tasksError;

      // Combina os resultados
      return {
        ...run,
        playbook_tasks: tasks || []
      } as PlaybookRun;
    } catch (error) {
      console.error('Erro ao buscar execução:', error);
      throw error;
    }
  },

  // Criar uma nova execução de playbook
  async create(run: Omit<PlaybookRun, 'id' | 'created_at' | 'updated_at'>): Promise<PlaybookRun> {
    const { data, error } = await supabase
      .from('playbook_runs')
      .insert(run)
      .select()
      .single();

    if (error) throw error;
    return data as PlaybookRun;
  },

  // Atualizar uma execução existente
  async update(id: string, run: Partial<PlaybookRun>): Promise<PlaybookRun> {
    const { data, error } = await supabase
      .from('playbook_runs')
      .update(run)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as PlaybookRun;
  },

  // Excluir uma execução
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_runs')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Serviços para Tarefas de Execução
export const playbookTaskService = {
  // Listar todas as tarefas de uma execução
  async getByRunId(runId: string): Promise<PlaybookTask[]> {
    const { data, error } = await supabase
      .from('playbook_tasks')
      .select('*')
      .eq('playbook_run_id', runId)
      .order('task_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Obter detalhes de uma tarefa específica incluindo comentários
  async getById(id: string): Promise<PlaybookTask | null> {
    try {
      // Primeiro busca a tarefa
      const { data: task, error: taskError } = await supabase
        .from('playbook_tasks')
        .select('*')
        .eq('id', id)
        .single();

      if (taskError) throw taskError;
      if (!task) return null;

      // Depois busca os comentários relacionados
      const { data: comments, error: commentsError } = await supabase
        .from('playbook_task_comments')
        .select('*')
        .eq('playbook_task_id', id)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      // Combina os resultados
      return {
        ...task,
        playbook_task_comments: comments || []
      } as PlaybookTask;
    } catch (error) {
      console.error('Erro ao buscar tarefa:', error);
      throw error;
    }
  },

  // Obter todas as tarefas atribuídas a um usuário
  async getByAssignee(userId: string): Promise<PlaybookTask[]> {
    const { data, error } = await supabase
      .from('playbook_tasks')
      .select(`
        *,
        playbook_runs(id, subscription_id, status)
      `)
      .eq('assigned_to', userId)
      .in('status', ['current', 'upcoming', 'pending', 'overdue'])
      .order('due_date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Atualizar uma tarefa existente
  async update(id: string, task: Partial<PlaybookTask>): Promise<PlaybookTask> {
    const { data, error } = await supabase
      .from('playbook_tasks')
      .update(task)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Excluir uma tarefa
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Serviços para Comentários de Tarefa
export const playbookTaskCommentService = {
  // Listar todos os comentários de uma tarefa
  async getByTaskId(taskId: string): Promise<PlaybookTaskComment[]> {
    const { data, error } = await supabase
      .from('playbook_task_comments')
      .select(`
        *,
        profiles:created_by(id, first_name, last_name, email)
      `)
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as PlaybookTaskComment[];
  },

  // Adicionar um novo comentário
  async create(comment: Omit<PlaybookTaskComment, 'id' | 'created_at' | 'updated_at'>): Promise<PlaybookTaskComment> {
    const { data, error } = await supabase
      .from('playbook_task_comments')
      .insert(comment)
      .select()
      .single();

    if (error) throw error;
    return data as PlaybookTaskComment;
  },

  // Excluir um comentário
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('playbook_task_comments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Serviços para Campos Personalizados de Tarefa
export const playbookTaskCustomFieldService = {
  // Listar todos os campos personalizados de uma tarefa
  async getByTaskId(taskId: string): Promise<PlaybookTaskCustomField[]> {
    const { data, error } = await supabase
      .from('playbook_task_custom_fields')
      .select('*')
      .eq('task_id', taskId);

    if (error) throw error;
    return data as PlaybookTaskCustomField[];
  },

  // Atualizar um campo personalizado
  async update(taskId: string, fieldName: string, fieldValue: string): Promise<PlaybookTaskCustomField> {
    const { data, error } = await supabase
      .from('playbook_task_custom_fields')
      .update({ field_value: fieldValue })
      .match({ task_id: taskId, field_name: fieldName })
      .select()
      .single();

    if (error) throw error;
    return data as PlaybookTaskCustomField;
  }
};

// Links Públicos
export const playbookPublicLinkService = {
  // Criar um link público para uma execução
  async create(
    playbookRunId: string, 
    createdBy: string,
    expiresAt?: string
  ): Promise<PlaybookPublicLink> {
    // Gerar um token único aleatório
    const token = Math.random().toString(36).substring(2, 15) + 
                  Math.random().toString(36).substring(2, 15);

    const { data, error } = await supabase
      .from('playbook_public_links')
      .insert({
        playbook_run_id: playbookRunId,
        token,
        expires_at: expiresAt,
        created_by: createdBy
      })
      .select()
      .single();

    if (error) throw error;
    return data as PlaybookPublicLink;
  },

  // Obter uma execução de playbook por token (para visualização pública)
  async getPlaybookRunByToken(token: string): Promise<PlaybookRun | null> {
    // Primeiro verificar se o link ainda é válido
    const { data: linkData, error: linkError } = await supabase
      .from('playbook_public_links')
      .select('*')
      .eq('token', token)
      .single();

    if (linkError) throw linkError;
    
    if (!linkData) return null;
    
    // Verificar expiração
    if (linkData.expires_at && new Date(linkData.expires_at) < new Date()) {
      return null;
    }

    // Buscar a execução relacionada
    const { data: runData, error: runError } = await supabase
      .from('playbook_runs')
      .select(`
        *,
        playbook_tasks(*)
      `)
      .eq('id', linkData.playbook_run_id)
      .single();

    if (runError) throw runError;
    return runData as PlaybookRun;
  }
};

// Serviço para usuários
export const userService = {
  // Listar todos os usuários
  async getAll(): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email')
      .order('first_name', { ascending: true });

    if (error) throw error;
    return data as User[];
  }
};

// Exportar todos os serviços de playbook
export const playbookService = {
  templates: playbookTemplateService,
  templateTaskGroups: playbookTemplateTaskGroupService,
  templateTasks: playbookTemplateTaskService,
  runs: playbookRunService,
  tasks: playbookTaskService,
  comments: playbookTaskCommentService,
  publicLinks: playbookPublicLinkService,
  users: userService,
  taskGroups: {
    create: async (group: Omit<PlaybookTaskGroup, "id" | "created_at" | "updated_at">): Promise<PlaybookTaskGroup> => {
      const { data, error } = await supabase
        .from("playbook_task_groups")
        .insert([group])
        .select()
        .single()

      if (error) throw error
      return data
    },

    getByRunId: async (runId: string): Promise<PlaybookTaskGroup[]> => {
      const { data, error } = await supabase
        .from("playbook_task_groups")
        .select("*")
        .eq("playbook_run_id", runId)
        .order("group_order", { ascending: true })

      if (error) throw error
      return data || []
    },

    update: async (id: string, group: Partial<PlaybookTaskGroup>): Promise<PlaybookTaskGroup> => {
      const { data, error } = await supabase
        .from("playbook_task_groups")
        .update(group)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },

    delete: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from("playbook_task_groups")
        .delete()
        .eq("id", id)

      if (error) throw error
    }
  }
}

export default playbookService; 